/**
 * Categorization Rules API Service (WP-CAT-12)
 *
 * Handles all categorization rule-related API calls.
 * Endpoint: /v1/categorization-rules/*
 *
 * API Source: services/core/app/api/v1/categorization_rules.py
 * Schema Source: services/core/app/schemas/categorization_rule.py
 *
 * IMPORTANT: This is a SEPARATE endpoint from /categories/.
 * The old methods in categories.ts (getRules, createRule, etc.) were dead code
 * calling non-existent endpoints.
 */
import api from './api';
import type {
  CategorizationRule,
  CategorizationRuleListResponse,
  RuleCreateRequest,
  RuleUpdateRequest,
  RuleTestRequest,
  RuleTestResponse,
  RuleListParams,
  PatternType,
} from '@/types/categorizationRule';

// ============================================================================
// API Response Types (handle both camelCase and snake_case)
// ============================================================================

interface ApiRule {
  id: string;
  name: string;
  pattern: string;
  patternType?: PatternType;
  pattern_type?: PatternType;
  categoryId?: string;
  category_id?: string;
  priority: number;
  confidence: number | string;
  isActive?: boolean;
  is_active?: boolean;
  isSystem?: boolean;
  is_system?: boolean;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
}

interface ApiRuleListResponse {
  rules: ApiRule[];
  total: number;
  limit: number;
  offset: number;
}

interface ApiTestMatch {
  ruleId?: string;
  rule_id?: string;
  ruleName?: string;
  rule_name?: string;
  categoryId?: string;
  category_id?: string;
  categoryName?: string | null;
  category_name?: string | null;
  confidence: number | string;
  priority: number;
  isSystem?: boolean;
  is_system?: boolean;
}

interface ApiTestResponse {
  text: string;
  patternType?: PatternType;
  pattern_type?: PatternType;
  matches: ApiTestMatch[];
  bestMatch?: ApiTestMatch | null;
  best_match?: ApiTestMatch | null;
}

// ============================================================================
// Transformation Functions
// ============================================================================

/**
 * Transform API rule response to frontend type.
 */
function transformRule(apiRule: ApiRule): CategorizationRule {
  return {
    id: apiRule.id,
    name: apiRule.name,
    pattern: apiRule.pattern,
    patternType: apiRule.patternType ?? apiRule.pattern_type ?? 'description',
    categoryId: apiRule.categoryId ?? apiRule.category_id ?? '',
    priority: apiRule.priority,
    confidence: Number(apiRule.confidence),
    isActive: apiRule.isActive ?? apiRule.is_active ?? true,
    isSystem: apiRule.isSystem ?? apiRule.is_system ?? false,
    createdAt: apiRule.createdAt ?? apiRule.created_at ?? '',
    updatedAt: apiRule.updatedAt ?? apiRule.updated_at ?? '',
  };
}

/**
 * Transform API test match to frontend type.
 */
function transformTestMatch(apiMatch: ApiTestMatch): import('@/types/categorizationRule').RuleTestMatch {
  return {
    ruleId: apiMatch.ruleId ?? apiMatch.rule_id ?? '',
    ruleName: apiMatch.ruleName ?? apiMatch.rule_name ?? '',
    categoryId: apiMatch.categoryId ?? apiMatch.category_id ?? '',
    categoryName: apiMatch.categoryName ?? apiMatch.category_name ?? null,
    confidence: Number(apiMatch.confidence),
    priority: apiMatch.priority,
    isSystem: apiMatch.isSystem ?? apiMatch.is_system ?? false,
  };
}

// ============================================================================
// Categorization Rules Service
// ============================================================================

export const categorizationRulesService = {
  /**
   * Get all visible categorization rules (system + tenant-specific).
   *
   * API: GET /categorization-rules
   * Source: services/core/app/api/v1/categorization_rules.py lines 50-94
   */
  getAll: async (params: RuleListParams = {}): Promise<CategorizationRuleListResponse> => {
    const queryParams: Record<string, unknown> = {};

    if (params.patternType) queryParams.patternType = params.patternType;
    if (params.isActive !== undefined) queryParams.isActive = params.isActive;
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.offset !== undefined) queryParams.offset = params.offset;

    const response = await api.get<ApiRuleListResponse>('/categorization-rules', queryParams);

    return {
      rules: response.rules.map(transformRule),
      total: response.total,
      limit: response.limit,
      offset: response.offset,
    };
  },

  /**
   * Get a single categorization rule by ID.
   *
   * API: GET /categorization-rules/{rule_id}
   * Source: services/core/app/api/v1/categorization_rules.py lines 97-137
   */
  getById: async (id: string): Promise<CategorizationRule> => {
    const response = await api.get<ApiRule>(`/categorization-rules/${id}`);
    return transformRule(response);
  },

  /**
   * Create a new custom categorization rule.
   *
   * API: POST /categorization-rules
   * Source: services/core/app/api/v1/categorization_rules.py lines 140-196
   */
  create: async (data: RuleCreateRequest): Promise<CategorizationRule> => {
    const response = await api.post<ApiRule>('/categorization-rules', data);
    return transformRule(response);
  },

  /**
   * Update an existing custom categorization rule.
   *
   * API: PATCH /categorization-rules/{rule_id}
   * Source: services/core/app/api/v1/categorization_rules.py lines 198-276
   *
   * Note: System rules cannot be updated (403 Forbidden).
   */
  update: async (id: string, data: RuleUpdateRequest): Promise<CategorizationRule> => {
    const response = await api.patch<ApiRule>(`/categorization-rules/${id}`, data);
    return transformRule(response);
  },

  /**
   * Delete a custom categorization rule.
   *
   * API: DELETE /categorization-rules/{rule_id}
   * Source: services/core/app/api/v1/categorization_rules.py lines 279-324
   *
   * Note: System rules cannot be deleted (403 Forbidden).
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/categorization-rules/${id}`);
  },

  /**
   * Test categorization rules against sample text.
   *
   * API: POST /categorization-rules/test
   * Source: services/core/app/api/v1/categorization_rules.py lines 327-384
   *
   * Returns all matching rules ordered by priority, plus the best match.
   */
  test: async (data: RuleTestRequest): Promise<RuleTestResponse> => {
    const response = await api.post<ApiTestResponse>('/categorization-rules/test', data);

    return {
      text: response.text,
      patternType: response.patternType ?? response.pattern_type ?? 'description',
      matches: response.matches.map(transformTestMatch),
      bestMatch: (response.bestMatch ?? response.best_match)
        ? transformTestMatch(response.bestMatch ?? response.best_match!)
        : null,
    };
  },
};

export default categorizationRulesService;
